import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Users, DollarSign, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getAnalytics } from '@/lib/api';
import { formatPrice, getImageUrl } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const res = await getAnalytics();
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 skeleton rounded w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-medium">Admin Dashboard</h1>
          <Link to="/admin/products" className="text-sm text-stone-600 hover:text-stone-900">
            Manage Products →
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-stone-600">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-stone-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-light" data-testid="total-revenue">
                {formatPrice(analytics?.revenue || 0)}
              </div>
              <p className="text-xs text-stone-500 mt-1">
                From {analytics?.paid_orders || 0} paid orders
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-stone-600">Total Orders</CardTitle>
              <ShoppingCart className="h-4 w-4 text-stone-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-light" data-testid="total-orders">
                {analytics?.total_orders || 0}
              </div>
              <p className="text-xs text-stone-500 mt-1">
                {analytics?.paid_orders || 0} paid, {(analytics?.total_orders || 0) - (analytics?.paid_orders || 0)} pending
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-stone-600">Customers</CardTitle>
              <Users className="h-4 w-4 text-stone-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-light" data-testid="total-customers">
                {analytics?.total_customers || 0}
              </div>
              <p className="text-xs text-stone-500 mt-1">Registered users</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-stone-600">Low Stock</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-light" data-testid="low-stock-count">
                {analytics?.low_inventory?.length || 0}
              </div>
              <p className="text-xs text-stone-500 mt-1">Products below 10 units</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Best Sellers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-medium">Best Sellers</CardTitle>
              <CardDescription>Top selling products</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.best_sellers?.slice(0, 5).map((product) => (
                  <div key={product.product_id} className="flex items-center gap-4">
                    <img
                      src={getImageUrl(product.images[0])}
                      alt={product.name}
                      className="w-12 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-light truncate">{product.name}</p>
                      <p className="text-xs text-stone-500">{product.collection}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-light">{product.sold} sold</p>
                      <p className="text-xs text-stone-500">{formatPrice(product.price)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-medium">Recent Orders</CardTitle>
              <CardDescription>Latest customer orders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.recent_orders?.slice(0, 5).map((order) => (
                  <Link
                    key={order.order_id}
                    to={`/admin/orders`}
                    className="flex items-center justify-between p-3 hover:bg-stone-50 rounded-lg transition-colors"
                  >
                    <div>
                      <p className="text-sm font-light">#{order.order_id}</p>
                      <p className="text-xs text-stone-500">{order.customer_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-light">{formatPrice(order.total)}</p>
                      <Badge variant="outline" className="text-xs mt-1">
                        {order.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Inventory Alert */}
        {analytics?.low_inventory && analytics.low_inventory.length > 0 && (
          <Card className="mt-8 border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Low Inventory Alert
              </CardTitle>
              <CardDescription>Products running low on stock</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {analytics.low_inventory.map((product) => (
                  <div key={product.product_id} className="flex items-center gap-4 p-3 bg-white rounded-lg">
                    <img
                      src={getImageUrl(product.images[0])}
                      alt={product.name}
                      className="w-12 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-light truncate">{product.name}</p>
                      <p className="text-xs text-stone-500">{product.collection}</p>
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      {product.inventory} left
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
